import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import './HoverPanel.css';
import { useViewportShift } from '../../hooks/useViewportShift';
import { HOVER_BRIDGE_VARS } from '../../utils/tooltipUtils';

/**
 * Box for the panels a hover opens — the search grid's result panels, the build
 * slots' item and set tooltips, the blacklist chips'. Carries the placement and
 * nothing else; the panel inside keeps its own chrome. `pos` is in viewport
 * coordinates.
 *
 * These panels are content-sized and are placed from an estimate, so the tall
 * ones would hang off the bottom of the screen — `useViewportShift` measures the
 * box once it is up and slides it back inside.
 *
 * `interactive` is for a panel the pointer is meant to walk into: the box takes
 * pointer events and spans the gap to its anchor (see HoverPanel.css). Deciding
 * that entering the panel is not leaving the anchor stays with the caller, which
 * owns the hover state — it gets the box through `ref` to test `relatedTarget`
 * against, and `onMouseLeave` for the other direction.
 *
 * An interactive box is placed inside the page, absolutely in its nearest
 * positioned ancestor, where a plain one is fixed over it: a fixed box hands a
 * wheel to the window, which never scrolls here, so one the pointer rests on
 * would freeze the page. Inside it, the page scrolls natively and the box rides
 * along with its anchor. The caller renders it within the scrolling area, under
 * a positioned ancestor (Search's `.search-results`).
 *
 * `centered` takes `pos.x` as the panel's centre rather than an edge — for a
 * stacked panel over a button, centred on it the way a simple tooltip is.
 */
const HoverPanel = React.forwardRef(({
    pos,
    interactive = false,
    stacked = false,
    centered = false,
    onMouseLeave,
    children,
}, ref) => {
    const innerRef = useRef(null);
    // Where the containing box's content starts on screen, to turn `pos` into
    // offsets within it. Taken once per placement: from then on the panel
    // scrolls with the page, as its anchor does.
    const [origin, setOrigin] = useState(null);
    useLayoutEffect(() => {
        const parent = interactive ? innerRef.current?.offsetParent : null;
        if (!parent) return;
        const r = parent.getBoundingClientRect();
        setOrigin({
            x: r.left + parent.clientLeft - parent.scrollLeft,
            y: r.top + parent.clientTop - parent.scrollTop,
        });
    }, [interactive, pos]);
    const placed = interactive ? origin : pos;
    const at = (interactive && origin) || { x: 0, y: 0 };
    const shift = useViewportShift(innerRef, placed);

    // Measured here, tested for `contains` by the caller — one node, two readers.
    const setRefs = useCallback((node) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    }, [ref]);

    return (
        <div
            ref={setRefs}
            className={`hover-panel${interactive ? ' hover-panel--interactive' : ''}${stacked ? ' hover-panel--stacked' : ''}`}
            style={{
                left: pos.x - at.x,
                top: pos.y - at.y + shift,
                visibility: placed ? undefined : 'hidden',
                transform: `${pos.goUpwards ? 'translateY(-100%)' : 'translateY(0)'} ${centered ? 'translateX(-50%)' : pos.goLeftwards ? 'translateX(-100%)' : 'translateX(0)'}`,
                ...(interactive ? HOVER_BRIDGE_VARS : null),
            }}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </div>
    );
});

export default HoverPanel;
